import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import { ReactElement, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as BillAnalytics from '../../../analytics/BillAnalytics';
import { BillAPI } from '../../../api';
import FeedbackPrompt from '../Prompts/FeedbackPrompt';
import { formatPlainTextToReact } from './utils';
import AiSummaryAnimatedText, { Loading } from '../../BillView/AiSummaryAnimatedText';
import { Bill } from '@enview/interface/types/bills/Bill';

type BillAutoSummaryProps = {
  bill: Bill;
  versionId: number;
};

/**
 * Demo component to fetch and display a generated bill summary
 * from the Data Insights API
 *
 * @param props
 * @constructor
 */
const BillAutoSummary = (props: BillAutoSummaryProps): ReactElement => {
  const { bill, versionId } = props;
  const {
    data: summaryResponse,
    isError,
    isLoading,
  } = BillAPI.endpoints.getBillAutoSummary.useQuery({
    billId: bill.id,
    billVersionId: versionId,
  });
  const { t } = useTranslation();
  const isCongressBill = bill.session.state.abbreviation === 'usa';

  const [isSummaryAccurate, setIsSummaryAccurate] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (isSummaryAccurate !== undefined && summaryResponse?.summary) {
      BillAnalytics.trackBillAutoSummaryFeedback(
        bill.id,
        versionId,
        isSummaryAccurate,
        summaryResponse.summary,
      );
    }
  }, [isSummaryAccurate, summaryResponse?.summary]);

  const getText = (): ReactElement[] | ReactElement | string => {
    if (summaryResponse && summaryResponse.insightsApiError) {
      return t(summaryResponse.errorResourceCode);
    }
    if (summaryResponse && summaryResponse.summary) {
      return (
        <>
          <p className="ai-disclaimer-message">{t('bill.sidebar.aiDisclaimer')}</p>
          {formatPlainTextToReact(summaryResponse.summary)}
          <FeedbackPrompt
            feedbackType={FeedbackType.BillSummaryInaccurate}
            setFeedbackState={setIsSummaryAccurate}
            submitFeedbackMetadata={{
              billId: bill.id,
              billVersionId: versionId,
              summary: summaryResponse.summary,
            }}
          />
        </>
      );
    }
    if (isError) {
      return t('error.genericApiErrorMessage');
    }
    return <></>;
  };

  if (isLoading) {
    return (
      <div className="m-auto mt-3">
        <div style={{ width: '25px', float: 'left' }}>
          <Spinner animation="border" role="status" variant="secondary" />
        </div>
        {isCongressBill && (
          <div style={{ marginLeft: '48px' }}>
            <AiSummaryAnimatedText loading={Loading.congressBillSummaries} />
          </div>
        )}
      </div>
    );
  }

  return <div className="ai-generated-text">{getText()}</div>;
};

export default BillAutoSummary;
